import moment from 'moment'
import React, { useCallback, useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import callAPI from '../../../../helpers/callAPI'
import useCallApi from '../../../../hooks/useCallApi'
import Loading from '../../../../templates/UI/Loading/Loading'
import ReportTable from '../../../../templates/ReportTable/ReportTable'

/** Strip "MB - Daily - " / "MB - Monthly - " etc. (prefix up to and including second " - ") */
const formatDbJobName = (name) => {
    if (!name || typeof name !== 'string') return name
    const sep = ' - '
    const i = name.indexOf(sep)
    if (i === -1) return name
    const j = name.indexOf(sep, i + sep.length)
    if (j === -1) return name
    const formatted = name.slice(j + sep.length).trim()

    const critSuffix = ' - CRIT'
    if (formatted.endsWith(critSuffix)) return formatted.slice(0, -critSuffix.length).trim()

    return formatted
}

const DbJobStatusTable = ({ showSystemMessage }) => {
    const { data, dataLoaded } = useCallApi('reports/dbJobReport?criticalOnly=true', 'GET', {}, [], showSystemMessage)
    const [logModalOpen, setLogModalOpen] = useState(false)
    const [selectedJobForLog, setSelectedJobForLog] = useState(null)
    const [logData, setLogData] = useState([])
    const [logLoading, setLogLoading] = useState(false)

    const jobs = data?.jobs || []

    const openLogModal = useCallback((job) => {
        setSelectedJobForLog({ jobId: job.job_id, jobName: job.job_name })
        setLogModalOpen(true)
        setLogData([])
    }, [])

    const closeLogModal = useCallback(() => {
        setLogModalOpen(false)
        setSelectedJobForLog(null)
        setLogData([])
    }, [])

    const stepIdRank = (stepId) => {
        const n = Number(stepId)
        if (!Number.isFinite(n)) return Number.MAX_SAFE_INTEGER
        return n === 0 ? Number.MAX_SAFE_INTEGER : n
    }

    const getJobLogRowProps = useCallback((row) => {
        const cycleIndex = Number.isFinite(row?.original?.run_cycle) ? row.original.run_cycle : 0
        return {
            className: cycleIndex % 2 === 0 ? 'db-job-log-cycle-light' : 'db-job-log-cycle-dark',
        }
    }, [])

    useEffect(() => {
        if (!logModalOpen || !selectedJobForLog) return
        setLogLoading(true)
        callAPI(`reports/dbJobReport/log/${selectedJobForLog.jobId}`, 'GET')
            .then((result) => {
                if (result.success && Array.isArray(result.log)) {
                    const latestCycle = result.log.reduce((maxCycle, row) => {
                        const cycle = Number(row?.run_cycle)
                        return Number.isFinite(cycle) ? Math.max(maxCycle, cycle) : maxCycle
                    }, Number.NEGATIVE_INFINITY)

                    const latestCycleLog = Number.isFinite(latestCycle) ? result.log.filter((row) => Number(row?.run_cycle) === latestCycle) : result.log
                    setLogData(latestCycleLog)
                } else {
                    showSystemMessage?.('error', result.errors?.message || 'Failed to load job log')
                    setLogData([])
                }
            })
            .catch(() => {
                showSystemMessage?.('error', 'Failed to load job log')
                setLogData([])
            })
            .finally(() => setLogLoading(false))
    }, [logModalOpen, selectedJobForLog?.jobId])

    return (
        <>
            <div className="card mb-3 overflow-auto">
                <div className="card-header d-flex justify-content-between">
                    <span className="font-weight-bold">DB Jobs CRIT</span>
                    <Link to="/system/dbJobHistory">Full Report</Link>
                </div>
                <div className="card-body p-0">
                    <table className="table table-striped table-card m-0">
                        <thead>
                            <tr className="text-secondary">
                                <th>Job</th>
                                <th>Frequency</th>
                                <th>Status</th>
                                <th>Duration</th>
                                <th>Last Run</th>
                            </tr>
                        </thead>
                        <tbody>
                            {!dataLoaded ? (
                                <tr>
                                    <td colSpan={5}>
                                        <Loading />
                                    </td>
                                </tr>
                            ) : (
                                jobs.map((job) => {
                                    const run = job.last_run
                                    const hasAttention = run?.runtime_attention === true
                                    return (
                                        <tr key={job.job_id}>
                                            <td>
                                                <button type="button" className="btn btn-link p-0 align-baseline" onClick={() => openLogModal(job)}>
                                                    {formatDbJobName(job.job_name)}
                                                </button>
                                            </td>
                                            <td style={{ fontSize: '0.87rem' }}>{job.job_frequency || '—'}</td>
                                            <td>
                                                <div className={run?.job_status === 'Error' || run?.job_status === 'Fail' ? 'badge badge-danger' : run?.job_status === 'finished' || run?.job_status === 'Success' ? 'badge badge-success' : run?.job_status === 'Note' ? 'badge badge-warning' : ''}>{run?.job_status ?? '—'}</div>
                                            </td>
                                            <td style={{ fontSize: '0.87rem' }}>
                                                {run?.run_duration != null ? (
                                                    <span style={hasAttention ? { color: '#dc3545', fontWeight: 600 } : undefined}>
                                                        {run.run_duration}
                                                        {hasAttention ? '  ⚠' : ''}
                                                    </span>
                                                ) : (
                                                    '—'
                                                )}
                                            </td>
                                            <td style={{ fontSize: '0.87rem' }}>{run?.job_end ? moment(run.job_end).format('YYYY-MM-DD HH:mm') : run?.job_start ? moment(run.job_start).format('YYYY-MM-DD HH:mm') : '—'}</td>
                                        </tr>
                                    )
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            <Modal show={logModalOpen} onHide={closeLogModal} size="xl" centered>
                <Modal.Header closeButton>
                    <Modal.Title>{selectedJobForLog ? `${selectedJobForLog.jobName} (${selectedJobForLog.jobId})` : ''}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {logLoading ? (
                        <Loading />
                    ) : logData.length === 0 ? (
                        <div className="p-3">No log entries for this job.</div>
                    ) : (
                        <div className="table-responsive db-job-log-cycles">
                            <style>{`
                                .db-job-log-cycles table tbody tr.db-job-log-cycle-light { background-color: #f8f9fa !important; }
                                .db-job-log-cycles table tbody tr.db-job-log-cycle-dark { background-color: #e2e6ea !important; }
                            `}</style>
                            <ReportTable
                                data={logData}
                                columns={[
                                    { Header: 'Cycle', accessor: 'run_cycle', Cell: ({ cell: { value } }) => (value != null ? value : '—') },
                                    {
                                        Header: 'Step ID',
                                        accessor: 'step_id',
                                        sortType: (rowA, rowB) => stepIdRank(rowA.original.step_id) - stepIdRank(rowB.original.step_id),
                                        Cell: ({ cell: { value } }) => (value != null ? value : '—'),
                                    },
                                    { Header: 'Step', accessor: 'step_name', Cell: ({ cell: { value } }) => value || '—' },
                                    { Header: 'Message', accessor: 'message', Cell: ({ cell: { value } }) => value || '—' },
                                    { Header: 'Run Date', accessor: 'run_date', Cell: ({ cell: { value } }) => value || '—' },
                                    { Header: 'Duration', accessor: 'run_duration', Cell: ({ cell: { value } }) => (value != null ? value : '—') },
                                    { Header: 'DB Date', accessor: 'db_date', Cell: ({ cell: { value } }) => value || '—' },
                                ]}
                                disableFilters
                                getRowProps={getJobLogRowProps}
                            />
                        </div>
                    )}
                </Modal.Body>
            </Modal>
        </>
    )
}

export default DbJobStatusTable
